import { action, computed, observable, runInAction } from 'mobx';
import { pick } from 'lodash';
import { Layout } from 'react-grid-layout';
import { Model, ModelSaveOptions } from 'core/model/Model';
import api from 'core/util/api';
import generateLayout, { generatePanelLayout, updateLayout } from 'app/stores/boards/generateLayout';
import isEqualLoose from 'app/util/isEqualsLoose';
import { getWidgetTitle } from 'app/views/boards/panels/widget/utils';
import { isLayoutEqual, PanelData, QueryFieldsInput, VariableMapping } from '@kentik/ui-shared/boards/panels';
import { titleExactMatches } from 'app/views/boards/panels/ude/metricsTreeData';
import { getQueryFieldsSignature } from 'app/views/boards/panels/ude/queryFieldsSignature';
import { WithOptional } from '@kentik/ui-shared/types/util';
import { showSuccessToast } from 'core/components';
import { Query } from '@kentik/ui-shared/query/query.types';
import { GRID_MARGIN, GRID_ROW_HEIGHT } from './constants';
import type BoardModel from './BoardModel';
import QueryModel from '../ude/QueryModel';

export type BoardPanelDefaults = 'title' | 'config' | 'id';

export interface BoardPanelJson extends WithOptional<
  PanelData,
  'company_id' | 'user_id' | 'board_id' | 'cdate' | 'edate'
> {
  draft?: boolean;
}

class BoardPanelModel extends Model<BoardPanelJson, BoardPanelDefaults> {
  // assigned by UdePanel
  @observable.ref queryModel?: QueryModel;

  // used to detect not-yet-run form edits.
  @observable lastAppliedQuerySignature?: string;

  @observable isDraftDeleting = false;

  // prevent layout updates while dragging
  isDragging = false;

  lastSaved?: WithOptional<BoardPanelJson, 'id'>;

  get urlRoot() {
    return `/api/ui/boards/${this.board.id}/panel`;
  }

  get defaults() {
    return {
      title: '',
      config: {
        // useful for Widgets where a titlebar may not be necessary
        showTitle: true,
        chart: {
          type: 'line'
        }
      }
    } as const;
  }

  get omitDuringSerialize(): (keyof BoardPanelJson)[] {
    return ['savedPanel'];
  }

  get board() {
    if (!this.collection?.parent) {
      throw new Error('Panel not associated with a board');
    }
    return this.collection.parent as unknown as BoardModel;
  }

  @computed
  get panels() {
    return this.board.panelCollection.getSubPanels(this.id);
  }

  // layout is stored on each sub panel

  @computed
  get layout() {
    return generateLayout(this.panels);
  }

  set layout(value) {
    const updatedPanels = updateLayout(this.panels, value);
    this.board.bulkSavePanels(updatedPanels);
  }

  @computed
  get isLayoutDirty() {
    if (!this.lastSaved) {
      return true;
    }

    const savedLayout = this.lastSaved.layout;
    const currentLayout = this.get('layout');
    return !isLayoutEqual(savedLayout, currentLayout);
  }

  /**
   * This panel has unsaved or draft changes
   */
  @computed
  get isDirty() {
    return this.get('draft') || (this.isReady && this.hasUnsavedChanges);
  }

  /**
   * Panel only exists as a draft (no saved non-draft version to revert to)
   */
  @computed
  get hasOnlyDraftVersion() {
    return this.get('draft') && !this.get('savedPanel');
  }

  @computed
  get hasUnsavedChanges() {
    if (!this.get('cdate') && !this.get('config').isDefault) {
      return true;
    }

    const panelFields = ['title', 'parent_panel_id', 'type', 'config'];
    const beforeFields = pick(this.lastSaved, panelFields);
    const afterFields = pick(this.serialize(), panelFields);

    return !isEqualLoose(beforeFields, afterFields);
  }

  /**
   * Panel is ready to be saved
   */
  @computed
  get isReady(): boolean {
    const type = this.get('type');

    if (type === 'ude') {
      return !!this.queryModel?.hasRun;
    }

    return true;
  }

  get title() {
    const title = this.get('title');
    const type = this.get('type');
    const config = this.get('config') || {};

    if (title) {
      return title;
    }
    if (config.title) {
      return config.title;
    }
    if (type === 'widget') {
      // For widgets with an embedded query (e.g. dataExplorerQuery), prefer the query's own title
      const queryConfig = config.query as { query_title?: string } | undefined;
      if (queryConfig?.query_title) {
        return queryConfig.query_title;
      }
      const widgetTitle = getWidgetTitle(config);
      if (widgetTitle) {
        return widgetTitle;
      }
      return 'Untitled Widget';
    }
    if (type === 'ude') {
      const measurement = this.queryModel?.query?.measurement;
      if (measurement) {
        const prefix = measurement.split('/')[1];
        const label = titleExactMatches[prefix];
        if (label) {
          return `Untitled ${label}`;
        }
      }
      return 'Untitled Draft';
    }

    return 'Untitled';
  }

  @computed
  get itemHeight() {
    const { h = 2 } = this.get('layout')[this.board.breakpoint] || {};
    return h * GRID_ROW_HEIGHT + (h - 1) * GRID_MARGIN;
  }

  @computed
  get presetFilters() {
    return this.board.get('config').presetFilters || [];
  }

  getVariableMapping(variableName: string) {
    const variableMappings = this.get('config').variableMappings || [];
    return variableMappings.find((mapping) => mapping.variableName === variableName);
  }

  toggleVariableMapping(variableName: string) {
    const config = this.get('config');
    const variableMappings = config.variableMappings || [];
    const existingMappingIndex = variableMappings.findIndex((mapping) => mapping.variableName === variableName);

    if (existingMappingIndex > -1) {
      variableMappings[existingMappingIndex].disabled = !variableMappings[existingMappingIndex].disabled;
    }

    this.safeSet({ config: { ...config, variableMappings } });
  }

  setVariableMapping(variableMapping: VariableMapping) {
    const { variableName } = variableMapping;
    const config = this.get('config');
    const variableMappings = config.variableMappings || [];
    const existingMappingIndex = variableMappings.findIndex((mapping) => mapping.variableName === variableName);

    if (existingMappingIndex > -1) {
      variableMappings[existingMappingIndex] = variableMapping;
    } else {
      variableMappings.push(variableMapping);
    }

    this.safeSet({ config: { ...config, variableMappings } });
  }

  getMeasurement(): string | undefined {
    const query = this.get('config').data as Query | undefined;
    return query?.measurement;
  }

  getBodyContentsHeight(layout: Layout[]) {
    if (this.get('config').collapsed) {
      return 0;
    }

    const currentLayout = layout || this.layout[this.board.breakpoint];
    if (!currentLayout || currentLayout.length === 0) {
      return 190; // this is what happens when there is zero items
    }

    return Math.max(
      ...currentLayout.map((item) => (item.y + item.h) * GRID_ROW_HEIGHT + (item.y + item.h - 1) * GRID_MARGIN)
    );
  }

  async addPanel({ title, type, config, parent_panel_id, layout }: PanelData) {
    const subPanel = this.board.panelCollection.forge({ title, type, config, parent_panel_id, layout });
    await subPanel.save({}, { draft: true });
    return subPanel;
  }

  deserialize(_data: WithOptional<BoardPanelJson, 'id'>) {
    const data = _data || this.get();
    data.layout = generatePanelLayout(data);
    data.id = `${data.id}`.startsWith('i_') ? undefined : data.id;
    this.lastSaved = data.savedPanel || data;

    // Baseline the applied-query signature from the loaded config (first load only). Guarded
    // so subsequent draft-save deserializations don't overwrite the snapshot taken at apply.
    if (this.lastAppliedQuerySignature === undefined) {
      this.lastAppliedQuerySignature = getQueryFieldsSignature(data.config?.data as QueryFieldsInput | undefined);
    }

    return super.deserialize(data);
  }

  serialize(data?: BoardPanelJson) {
    data = data || this.get();
    data.layout = generatePanelLayout(data);
    return super.serialize(data);
  }

  @action
  applyQueryConfig(config: BoardPanelJson['config']) {
    this.safeSet({ config });
    this.lastAppliedQuerySignature = getQueryFieldsSignature(config?.data as QueryFieldsInput | undefined);
  }

  private pendingSave: {
    options: ModelSaveOptions & { draft?: boolean };
  } | null = null;

  private savePromise: Promise<unknown> | null = null;

  isDiscardingDraft = false;

  @action
  async save(
    attributes: Partial<PanelData>,
    options: ModelSaveOptions & { draft?: boolean } = {}
  ): Promise<BoardPanelJson> {
    const { draft = false } = options;

    // makes the save button show the loading state appropriately
    this.safeSet({ draft });

    if (this.savePromise) {
      this.safeSet(attributes);

      // don't overwrite a pending non-draft
      if (!this.pendingSave || this.pendingSave.options.draft) {
        this.pendingSave = { options };
      }

      await this.savePromise;

      if (this.pendingSave) {
        const { options: nextOptions } = this.pendingSave;
        this.pendingSave = null;
        return this.save({}, nextOptions);
      }

      return this.get();
    }

    if (this.board.isNew) {
      this.set(attributes);

      // creating the board with the new panel
      // will create a draft board if this is a draft panel
      return this.trackSave(this.board.save({}, options));
    }

    if (draft) {
      // save with the draft flag
      options.url = `${options.url || this.url}/?draft=true`;
      options.toast = false;
    }

    return this.trackSave(super.save(attributes, options));
  }

  private async trackSave(savePromise: Promise<unknown>) {
    this.savePromise = savePromise;

    try {
      await this.savePromise;
    } finally {
      this.savePromise = null;
    }

    return this.get();
  }

  async whenSaveSettled(): Promise<void> {
    let pending = this.savePromise;
    while (pending) {
      // eslint-disable-next-line no-await-in-loop -- intentionally drain the serialized save chain
      await pending.catch(() => undefined);
      pending = this.savePromise;
    }
  }

  async discardDraft({ toast = true }: { toast?: boolean } = {}) {
    if (this.isNew) {
      // nothing to do
      return;
    }

    const response: PanelData | { success: true } = await api.put(`${this.url}/discardDraft`);

    if ('success' in response) {
      // panel removed (no saved state)
      this.collection?.remove(this.id);
    } else {
      this.isDiscardingDraft = true;
      try {
        this.safeSet(this.deserialize(response));
        this.lastAppliedQuerySignature = getQueryFieldsSignature(
          this.get('config')?.data as QueryFieldsInput | undefined
        );
      } finally {
        this.isDiscardingDraft = false;
      }
    }

    if (toast) {
      await showSuccessToast(`Reverted panel "${this.title}"`);
    }
  }

  @action
  async draftDelete() {
    if (this.isNew) {
      this.collection?.remove(this.id);
      return;
    }

    if (this.isDraftDeleting) {
      return;
    }

    this.isDraftDeleting = true;

    try {
      await api.del(`${this.url}?draft=true`);
      runInAction(() => {
        this.collection?.remove(this.id);
      });
    } finally {
      runInAction(() => {
        this.isDraftDeleting = false;
      });
    }
  }
}

export default BoardPanelModel;
